import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { TextGridProps } from './TextGrid.data';
import styles from './TextGrid.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './TextGrid.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';

export default function TextGrid({ isVisible, className, blok, ref }: TextGridProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const itemsCount = blok.text_items.length;

  return (
    <div ref={elementRef} className={classNames(styles.textGrid, className)} {...sbEditable(blok)}>
      {blok.title && itemsCount > 0 && (
        <Heading
          as="h4"
          size="heading-04"
          className={classNames(styles.title)}
          transitionInType="slideUpFadeIn"
          disableTriggerInOnScroll={false}
        >
          {blok.title}
        </Heading>
      )}

      <div
        className={classNames(styles.flexContainer, styles.threeColumn, {
          [styles.fourColumn]:
            itemsCount % 4 === 0 || (itemsCount % 3 !== 0 && itemsCount % 3 !== 2),
        })}
      >
        {blok.text_items.map((text) => (
          <div key={text._uid}>
            <Heading
              as="h5"
              size="heading-05"
              transitionInType="splitLineRotateIn"
              className={styles.label}
              disableTriggerInOnScroll={false}
            >
              {text.label}
            </Heading>
            <Text
              className={styles.description}
              as="p"
              size="body-standard"
              disableTriggerInOnScroll={false}
            >
              {text.description}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
